/**
 * CTA Overlay Component
 *
 * AR体験中に表示するCTAボタンのオーバーレイ。
 * 全ポスター共通デザイン: 白地・黒文字・角丸1種類。
 * 画面下部固定、フェードイン+スライドアップで登場、マーカーロストで非表示。
 *
 * 表示制御は posters.json の `cta` フィールドに URL が入っていれば有効、
 * 無ければ null を返して非表示となる。旧 `overlayHtml` フィールドは
 * 後方互換で受ける（tabcode251129 等の既存データを壊さないため）。
 *
 * click 時は R2 分析に cta_click を積んですぐ flush する（外部URL遷移で
 * ページが消える前に確実に送信させるため）。
 *
 * @example
 * const cta = new CTAOverlay({ url: 'https://...', label: '詳細はこちら' });
 * cta.mount(document.getElementById('ui-container'));
 * cta.show();
 * cta.hide();
 */

import { flushToR2, trackCTAClick } from '../utils/analytics.js';

/** 未指定時のデフォルトラベル */
const DEFAULT_LABEL = '詳細はこちら';

export class CTAOverlay {
  /**
   * @param {Object} config
   * @param {string} [config.url] - 遷移先URL（http/https/tel/mailto等）
   * @param {string} [config.label] - ボタンラベル（未指定時は "詳細はこちら"）
   * @param {string} [config.legacyHtml] - 後方互換用の生HTML（旧 overlayHtml）
   */
  constructor(config = {}) {
    this.url = config.url || null;
    this.label = config.label || DEFAULT_LABEL;
    this.legacyHtml = config.legacyHtml || null;

    this.wrapper = null;
    this.isVisible = false;
    this.isMounted = false;
    // 動画完視聴後の固定表示モード。true の間は hide() を無効化して
    // マーカーロストで消えないようにする(消えるとクリック機会喪失につながるため)。
    this.isPinned = false;
  }

  /**
   * DOMを構築して親要素にマウントする
   * @param {HTMLElement} parentElement - 通常 #ui-container
   */
  mount(parentElement) {
    if (!parentElement) {
      console.warn('CTAOverlay: parentElement is required for mount()');
      return;
    }

    const existing = parentElement.querySelector('#action-overlay');
    if (existing) {
      existing.remove();
    }

    this.wrapper = document.createElement('div');
    this.wrapper.id = 'action-overlay';
    this.wrapper.className = 'cta-overlay';
    this.wrapper.setAttribute('aria-hidden', 'true');

    if (this.legacyHtml) {
      this.wrapper.innerHTML = this.legacyHtml;
    } else if (this.url) {
      this.wrapper.appendChild(this._buildButton());
    }

    parentElement.appendChild(this.wrapper);
    this.isMounted = true;
  }

  /**
   * 単一ボタンのDOMを生成（白地・黒文字・角丸）
   * @returns {HTMLElement}
   * @private
   */
  _buildButton() {
    const isExternalLink = /^https?:/i.test(this.url);
    const anchor = document.createElement('a');
    anchor.className = 'cta-button';
    anchor.href = this.url;

    if (isExternalLink) {
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
    }

    const labelSpan = document.createElement('span');
    labelSpan.className = 'cta-button-label';
    labelSpan.textContent = this.label;
    anchor.appendChild(labelSpan);

    // クリックを R2 分析に記録して即 flush（外部URL遷移前に送信を確実にするため）
    anchor.addEventListener('click', () => {
      trackCTAClick(this.url);
      flushToR2();
    });

    return anchor;
  }

  show() {
    if (!this.wrapper || this.isVisible) {
      return;
    }
    this.wrapper.classList.add('is-visible');
    this.wrapper.setAttribute('aria-hidden', 'false');
    this.isVisible = true;
  }

  hide() {
    if (!this.wrapper || !this.isVisible) {
      return;
    }
    // 固定表示モード中はマーカーロスト由来の hide を無効化。
    // 明示的にリセットしたい場合は unpin() 経由(現状呼び出し無し)。
    if (this.isPinned) {
      return;
    }
    this.wrapper.classList.remove('is-visible');
    this.wrapper.setAttribute('aria-hidden', 'true');
    this.isVisible = false;
  }

  /**
   * 動画完視聴後などに CTA を固定表示にする。
   * 呼び出し後は show() を強制実行し、以降 hide() は効かなくなる。
   * マーカーロストで CTA が消えてクリック機会を失う問題を回避するための入り口。
   */
  pin() {
    this.isPinned = true;
    this.show();
  }

  /**
   * 固定表示モードを解除する。通常フロー内での呼び出し想定は無い(セッション終了で消える)。
   */
  unpin() {
    this.isPinned = false;
  }

  getIsPinned() {
    return this.isPinned;
  }

  getIsVisible() {
    return this.isVisible;
  }

  hasContent() {
    return !!this.url || !!this.legacyHtml;
  }

  cleanup() {
    if (this.wrapper && this.wrapper.parentElement) {
      this.wrapper.parentElement.removeChild(this.wrapper);
    }
    this.wrapper = null;
    this.isVisible = false;
    this.isMounted = false;
    this.isPinned = false;
  }
}

/**
 * posterData から CTAOverlay を生成するファクトリ。
 * 新形式 `cta.url` を優先、旧 `overlayHtml` は後方互換で受ける。
 *
 * @param {Object} posterData
 * @returns {CTAOverlay|null} CTA表示不要なら null
 */
export function createCTAFromPosterData(posterData) {
  if (!posterData) {
    return null;
  }

  if (posterData.cta && typeof posterData.cta.url === 'string' && posterData.cta.url) {
    return new CTAOverlay({
      url: posterData.cta.url,
      label: posterData.cta.label,
    });
  }

  if (posterData.overlayHtml) {
    return new CTAOverlay({ legacyHtml: posterData.overlayHtml });
  }

  return null;
}
